import React, { useCallback, useMemo, useRef, useState } from 'react'
import { View } from 'react-native'
import {
  BtGoogleAutocomplete,
  BtInput,
  BtPhoneInput,
} from '@linqpal/components/src/ui'
import { useTranslation } from 'react-i18next'
import { ContractorModel as Model } from '@linqpal/models'
import { City, State, Zip } from '../components'
import { validateZip } from '@linqpal/models/src/helpers/validations'

const PrimeContractorDetails = ({ model, value }) => {
  const { t } = useTranslation('global')
  const ref = useRef<null | HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)

  const validatePhone = useCallback(
    () =>
      Model.create(value).phoneFilled ? '' : t('ProjectForm.phone-invalid'),
    [t, value],
  )

  const validateEmail = useCallback(
    () =>
      Model.create(value).emailFilled ? '' : t('ProjectForm.email-invalid'),
    [t, value],
  )

  const address = useMemo(() => {
    const { address: adr, city, state, zip } = value.businessAddress ?? {}
    if (adr && city && state && zip) {
      return `${adr} ${city} ${state} ${zip}`
    } else {
      return ''
    }
  }, [value.businessAddress])

  const onChangeText = useCallback(
    (field, val) => {
      model.setModelValue(field, val)
    },
    [model],
  )

  return (
    <>
      <div ref={ref} />
      <BtInput
        value={value.businessName}
        onChangeText={(val) => onChangeText('businessName', val)}
        testID="businessName"
        label={t('ProjectForm.business-name-label')}
        onFocus={() => ref.current?.scrollIntoView({ behavior: 'smooth' })}
        mandatory
      />
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
          marginTop: 20,
        }}
      >
        <BtInput
          value={value.firstName}
          onChangeText={(val) => onChangeText('firstName', val)}
          testID="firstName"
          label={t('ProjectForm.first-name-label')}
          style={{ width: '48%' }}
          mandatory
        />
        <BtInput
          value={value.lastName}
          onChangeText={(val) => onChangeText('lastName', val)}
          testID="lastName"
          label={t('ProjectForm.last-name-label')}
          style={{ width: '48%' }}
          mandatory
        />
      </View>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
          marginTop: 20,
        }}
      >
        <BtPhoneInput
          value={value.phone}
          testID="contractorPhone"
          label={t('ProjectForm.business-phone')}
          validate={validatePhone}
          onChangeText={(val) => onChangeText('phone', val.parsed)}
          style={{ width: '48%' }}
          required
        />
        <BtInput
          value={value.email}
          onChangeText={(val) => onChangeText('email', val)}
          label={t('ProjectForm.email-label')}
          testID="contractorEmail"
          validateInput={validateEmail}
          validateInputOnBlur
          mandatory
          style={{ width: '48%' }}
        />
      </View>
      <BtGoogleAutocomplete
        value={address}
        style={{ marginTop: 20 }}
        onSelect={(val) => {
          onChangeText('businessAddress', val)
          validateZip(val.zip) && val.city && setExpanded(false)
        }}
        onChange={(text: string) =>
          onChangeText('businessAddress', {
            ...value.businessAddress,
            address: text,
          })
        }
        label={t('ProjectForm.address-label')}
        onFocus={() => setExpanded(true)}
        mandatory
      />
      {expanded && (
        <>
          <State
            value={value.businessAddress?.state}
            onChangeText={(val) =>
              onChangeText('businessAddress', {
                ...value.businessAddress,
                state: val,
              })
            }
          />
          <City
            value={value.businessAddress?.city}
            onChangeText={(val) =>
              onChangeText('businessAddress', {
                ...value.businessAddress,
                city: val ?? '',
              })
            }
            style={{ marginTop: 20 }}
          />
          <Zip
            value={value.businessAddress?.zip}
            onChangeText={(val) =>
              onChangeText('businessAddress', {
                ...value.businessAddress,
                zip: val ?? '',
              })
            }
            style={{ marginTop: 20 }}
          />
        </>
      )}
    </>
  )
}

export default {
  identifier: 'primeContractorDetails',
  title: 'Tell us more about the prime contractor',
  component: PrimeContractorDetails,
  Model,
  optional: true,
}
